import type { ReactNode } from 'react'

import {
  Flex,
  SideMenuLayoutContentLayout,
} from '@enechain/ecloud-designsystem'

import { PageBreadcrumb, type PageBreadcrumbItem } from './PageBreadcrumb'
import { PageNoticeFrame } from './PageNoticeFrame'

/**
 * header xor breadcrumbItems の型排他 variant (frontend-design-unification 設計書 §5.1)。
 * - breadcrumbItems 形: 既存 28 caller が使う標準ページヘッダ (breadcrumb + 任意の headerExtra)
 * - header 形: ワークスペース系の完全カスタムヘッダ (日付ナビ / フェーズ切替) 専用の脱出口
 * ページ外周 padding は ecloud SideMenuLayoutContentLayout の既定値に固定 (prop 化しない)。
 */
type Props = {
  children: ReactNode
  /**
   * ページ最上部の通知 (Compact Alert 等)。渡した時だけ、本文の先頭に置き、コンテンツ枠の
   * 左・上・右の 16px を打ち消して枠の端に寄せる (複数の間隔は 0。本文は内側 16px のまま)。
   * 通知の置き場所・並べる順は UI 辞書 alert-placement.md §0・§1・§2。省略時 (undefined / null / false) は
   * 従来どおりの見た目 (枠は `display: contents` で箱を持たない)。通知の有無が変わっても本文は作り直さない。
   */
  notices?: ReactNode
} & (
  | {
      breadcrumbItems: PageBreadcrumbItem[]
      /**
       * page-header 右端に表示する追加要素 (design `data-page-header-extras` 相当。
       * 例: GC カウントダウン)。指定時のみ breadcrumb と横並びで描画する。
       */
      headerExtra?: ReactNode
      header?: never
    }
  | { header: ReactNode; breadcrumbItems?: never; headerExtra?: never }
)

export const PageLayout = (props: Props): React.ReactElement => {
  const { children, notices } = props
  const header =
    'header' in props ? (
      props.header
    ) : props.headerExtra === undefined ? (
      <PageBreadcrumb items={props.breadcrumbItems} />
    ) : (
      <Flex alignItems="center" justifyContent="space-between" width="100%">
        <PageBreadcrumb items={props.breadcrumbItems} />
        {props.headerExtra}
      </Flex>
    )
  return (
    <SideMenuLayoutContentLayout header={header}>
      {/* 通知の有無で木を変えない (本文が作り直されないよう、無い時も同じ枠を display: contents で置く) */}
      <PageNoticeFrame notices={notices}>{children}</PageNoticeFrame>
    </SideMenuLayoutContentLayout>
  )
}
